import { useMemo, useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useWindowWidth } from "../../hooks/useWindowWidth";
import ModalConfirmLogout from "../../components/ModalConfirmLogout";
import Header from "../../components/Header";
import "./HomeNH.css";
import "./TableGameResult.css";
import axios from "axios";
import Cookies from "js-cookie";
import Swal from "sweetalert2";

/** Mức cược cố định sảnh nổ hũ — chỉ gợi ý trong danh sách này */
const SLOT_BET_LEVELS = [
  0.4, 0.8, 1.2, 1.6, 2, 2.4, 2.8, 3.2, 3.6, 4,
  8, 12, 16, 20, 24, 28, 32, 36, 40,
  60, 100, 160, 200, 500, 1000,
] as const;

type SlotBetLevel = (typeof SLOT_BET_LEVELS)[number];

const indexOfSlotBet = (level: number) => {
  const i = SLOT_BET_LEVELS.findIndex((l) => Math.abs(l - level) < 0.001);
  return i >= 0 ? i : 0;
};

const formatSlotBetLevel = (level: number) => {
  if (!Number.isFinite(level) || level <= 0) return "0";
  const match = SLOT_BET_LEVELS.find((l) => Math.abs(l - level) < 0.001) ?? level;
  return match % 1 !== 0 ? String(match) : String(Math.floor(match));
};

/** Chọn mức cược lớn nhất trong danh sách mà không vượt target */
const snapSlotBetAtMost = (target: number, maxLevel?: number): SlotBetLevel => {
  const cap = maxLevel ?? SLOT_BET_LEVELS[SLOT_BET_LEVELS.length - 1];
  let picked: SlotBetLevel = SLOT_BET_LEVELS[0];
  for (const l of SLOT_BET_LEVELS) {
    if (l > cap) break;
    if (l <= target + 0.001) picked = l;
    else break;
  }
  return picked;
};

/**
 * Chia vốn (đơn vị K, sau khi làm tròn input VND) thành mức cược quay mồi / auto.
 * Đảm bảo tổng chi phị ước tính không vượt ~92% vốn và auto >= mồi (nếu có bậc cao hơn).
 */
const pickSlotBetsForCapital = (
  capitalK: number,
  manualRounds: number,
  autoRounds: number
) => {
  const capital = Math.max(capitalK, 1);
  const mR = Math.max(manualRounds, 1);
  const aR = Math.max(autoRounds, 1);
  const maxSpend = capital * 0.92;

  const manualTarget = ((maxSpend * mR) / (mR + aR)) / mR;
  const autoTarget = ((maxSpend * aR) / (mR + aR)) / aR;

  let manualBet: SlotBetLevel = snapSlotBetAtMost(manualTarget);
  let autoBet: SlotBetLevel = snapSlotBetAtMost(Math.max(autoTarget, manualBet));

  const spend = () => mR * manualBet + aR * autoBet;

  while (spend() > maxSpend + 0.001) {
    const ai = indexOfSlotBet(autoBet);
    if (ai > 0) {
      autoBet = SLOT_BET_LEVELS[ai - 1];
      if (autoBet < manualBet) autoBet = manualBet;
      continue;
    }
    const mi = indexOfSlotBet(manualBet);
    if (mi > 0) {
      manualBet = SLOT_BET_LEVELS[mi - 1];
      const aiAfter = indexOfSlotBet(autoBet);
      autoBet = SLOT_BET_LEVELS[Math.min(aiAfter, mi)];
      continue;
    }
    break;
  }

  const mi = indexOfSlotBet(manualBet);
  let ai = indexOfSlotBet(autoBet);
  if (ai < mi && mi < SLOT_BET_LEVELS.length - 1) {
    autoBet = SLOT_BET_LEVELS[mi + 1];
    while (spend() > maxSpend + 0.001 && ai > mi) {
      autoBet = SLOT_BET_LEVELS[ai - 1];
      ai = indexOfSlotBet(autoBet);
    }
  }

  return { manual: manualBet, auto: autoBet };
};

const TableGameNew = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isFishingTable = location.pathname.startsWith("/fishing/table");
  const params = useParams();
  const tableRoomParam = params?.room;
  const userInfo = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user_info") || "null");
    } catch {
      return null;
    }
  }, []);

  const [userCoins, setUserCoins] = useState<number>(userInfo?.coins ?? 0);
  const [isShowLogout, setIsShowLogout] = useState(false);
  const gameImg: string = (localStorage.getItem("title_img") || "/assets/phantichchitiet.gif") as string;
  const [gameTitle, setGameTitle] = useState<string>("THỢ SĂN CÁ MẬP");
  const winPercent = parseInt(localStorage.getItem("win_percent") || "90");

  const [points, setPoints] = useState<string>("");
  const [isHackPopupOpen, setIsHackPopupOpen] = useState(false);
  const [hackBalance, setHackBalance] = useState<string>("0");
  const [hackPopupMode, setHackPopupMode] = useState<"input" | "loading">("input");
  const [hackProgress, setHackProgress] = useState<number>(0);
  const [hackCapitalLabel, setHackCapitalLabel] = useState<string>("");
  const [isSpinning, setIsSpinning] = useState(false);
  const [isVipPopupOpen, setIsVipPopupOpen] = useState(false);
  const [vipRecommendBet, setVipRecommendBet] = useState<string>("8K");
  const [vipPctBig, setVipPctBig] = useState<number>(88);
  const [vipPctSuper, setVipPctSuper] = useState<number>(88);
  const [vipPctUltra, setVipPctUltra] = useState<number>(92);
  const [isVipFeatureCharging, setIsVipFeatureCharging] = useState(false);
  const [isVipHackPopupStarting, setIsVipHackPopupStarting] = useState(false);
  const [vipHackActive, setVipHackActive] = useState(false);
  const [vipHackUiVisible, setVipHackUiVisible] = useState(false);
  const [vipHackEndsAtMs, setVipHackEndsAtMs] = useState<number | null>(null);
  const [vipHackRemainingSec, setVipHackRemainingSec] = useState<number>(0);
  const [manualValues, setManualValues] = useState({ rounds: 0, minBet: "0" });
  const [autoValues, setAutoValues] = useState({ rounds: 0, minBet: "0" });
  const [bossValues, setBossValues] = useState({ rounds: 0 });
  const [fishingAnalysisComplete, setFishingAnalysisComplete] = useState(false);
  const [analysisLatencyMs, setAnalysisLatencyMs] = useState(12);
  const pendingFishingCapitalVndRef = useRef<number | null>(null);
  const [timeSlotText, setTimeSlotText] = useState(">Chưa có dữ liệu");
  const hackProgressTimerRef = useRef<number | null>(null);
  const hackRoundedValueRef = useRef<number | null>(null);
  const vipHackUiTimerRef = useRef<number | null>(null);

  const ww = useWindowWidth();
  const isNarrow = ww <= 430;
  const isMobileUi = ww <= 640;

  useEffect(() => {
    const raw = localStorage.getItem("user_info");
    if (raw) {
      try {
        setUserCoins(JSON.parse(raw)?.coins ?? 0);
      } catch {}
    }
  }, []);

  useEffect(() => {
    const titleFromStorage = localStorage.getItem("title_text");
    if (titleFromStorage) setGameTitle(titleFromStorage);
  }, [tableRoomParam]);

  useEffect(() => {
    Swal.fire({
      html: `<div style="padding: 18px; color:white; font-family: 'Courier New', monospace;">Đang tải dữ liệu...</div>`,
      showConfirmButton: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      timer: 1200,
      customClass: { popup: "custom-swal" },
    });
  }, []);

  useEffect(() => {
    setFishingAnalysisComplete(false);
    setBossValues({ rounds: 0 });
  }, [isFishingTable, tableRoomParam]);

  const deductCoins = async (amount: number = 1, action: string = "PLAY_GAME") => {
    try {
      const token = Cookies.get("access_token");
      if (!token) return true; // không chặn luồng khi dev chưa login
      const res = await axios.post(
        `${process.env.REACT_APP_URL_API}/users/subtract-coins-for-action`,
        { amount, action },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      const newCoins = res.data?.coins ?? userCoins;
      setUserCoins(newCoins);
      const raw = localStorage.getItem("user_info");
      if (raw) {
        const u = JSON.parse(raw); u.coins = newCoins; localStorage.setItem("user_info", JSON.stringify(u));
      }
      return true;
    } catch (e) {
      return false;
    }
  };

  const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
  const randomInt = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;
  // Gợi ý cho nổ hũ: quay mồi ngắn + min thấp, auto dài hơn + min vừa phải.
  // Tránh scale tuyến tính theo điểm nhập để không ra thông số quá đà.
  const generateSpinSuggestions = (
    target: number,
    coins: number,
    strategyHint?: "manual" | "auto"
  ) => {
    const safeTarget = Math.max(target, 10);
    const bankroll = Math.max(coins, safeTarget * 8, 200);
    const targetPressure = safeTarget / bankroll;

    // Nếu mục tiêu cao hơn sức vốn => ép về cấu hình bảo toàn vốn.
    const isConservative = targetPressure > 0.2;

    let manualRoundsRange: [number, number];
    let autoRoundsRange: [number, number];

    if (safeTarget <= 300) {
      manualRoundsRange = [8, 16];
      autoRoundsRange = [24, 42];
    } else if (safeTarget <= 1500) {
      manualRoundsRange = [12, 20];
      autoRoundsRange = [32, 56];
    } else if (safeTarget <= 5000) {
      manualRoundsRange = [16, 26];
      autoRoundsRange = [42, 72];
    } else {
      manualRoundsRange = [20, 32];
      autoRoundsRange = [52, 90];
    }

    if (isConservative) {
      manualRoundsRange = [manualRoundsRange[0] + 2, manualRoundsRange[1] + 4];
      autoRoundsRange = [autoRoundsRange[0] + 6, autoRoundsRange[1] + 8];
    }

    const manualRounds = randomInt(manualRoundsRange[0], manualRoundsRange[1]);
    let autoRounds = randomInt(autoRoundsRange[0], autoRoundsRange[1]);

    // Bias cho mục tiêu: "mồi" thì ưu tiên manual hơn, "auto" thì ưu tiên auto hơn.
    if (strategyHint === "manual") {
      // Đảm bảo auto < manual + 10 để buildTimeSlotText ra "ưu tiên mồi".
      autoRounds = Math.max(1, manualRounds + randomInt(-10, 5));
    } else if (strategyHint === "auto") {
      // Đảm bảo auto >= manual + 10 để buildTimeSlotText ra "ưu tiên auto".
      autoRounds = manualRounds + 10 + randomInt(0, 12);
    }

    // Vốn dùng đơn vị K (input VND đã /1000); mức cược snap theo SLOT_BET_LEVELS
    const capitalK = Math.max(
      safeTarget,
      Math.floor(bankroll / 1000) || safeTarget
    );
    const { manual: manualBet, auto: autoBet } = pickSlotBetsForCapital(
      capitalK,
      manualRounds,
      autoRounds
    );

    return {
      manual: { rounds: manualRounds, min: manualBet },
      auto: { rounds: autoRounds, min: autoBet },
    };
  };

  /** Chia ngẫu nhiên số lượt theo vốn (bắn cá) — auto thường lớn nhất. */
  const generateFishingTurnSplit = (capitalVnd: number) => {
    const c = Math.max(1000, Math.floor(capitalVnd));
    const k = Math.sqrt(clamp(c / 25000, 0.25, 6));
    const small = Math.max(8, Math.floor(randomInt(14, 20) * k));
    const boss = Math.max(10, Math.floor(randomInt(16, 24) * k));
    const autoBase = Math.floor(randomInt(42, 58) * k);
    const auto = Math.max(autoBase, small + boss + randomInt(18, 40));
    return { small, auto, boss };
  };

  const formatHourMinute = (date: Date) => {
    const hh = String(date.getHours()).padStart(2, "0");
    const mm = String(date.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  };

  const buildTimeSlotText = (manualRounds: number, autoRounds: number) => {
    const now = new Date();
    const startDelay = randomInt(1, 4); // bắt đầu sau 1-4 phút
    const strategyIsAuto = autoRounds >= manualRounds + 10;
    const duration = strategyIsAuto ? randomInt(18, 25) : randomInt(12, 18);

    const start = new Date(now.getTime() + startDelay * 60 * 1000);
    const end = new Date(start.getTime() + duration * 60 * 1000);
    const strategy = strategyIsAuto ? "ưu tiên auto" : "ưu tiên mồi";

    return `>${formatHourMinute(start)} - ${formatHourMinute(end)} (${strategy})`;
  };

  // Actions
  const handleStart = async (
    targetPoints?: number,
    tokenCost: number = 1,
    strategyHint?: "manual" | "auto",
    fishingCapitalVnd?: number
  ) => {
    const useFishingCapital =
      isFishingTable &&
      fishingCapitalVnd != null &&
      Number.isFinite(fishingCapitalVnd) &&
      fishingCapitalVnd >= 1000;

    if (useFishingCapital) {
      // vốn bắn cá — validate đã xử lý ở popup
    } else {
      const inputRaw =
        targetPoints !== undefined ? String(targetPoints) : points.trim();
      if (!inputRaw) {
        Swal.fire({
          icon: "warning",
          title: "Thiếu dữ liệu",
          text: isFishingTable
            ? "Vui lòng nhập số vốn trước khi bấm BẮT ĐẦU QUÉT."
            : "Vui lòng nhập điểm trước khi bấm BẮT ĐẦU PHÂN TÍCH.",
          confirmButtonText: "Đã hiểu",
        });
        return;
      }

      const input = parseInt(inputRaw, 10);
      if (!input || input <= 0) {
        Swal.fire({
          icon: "warning",
          title: isFishingTable ? "Vốn chưa hợp lệ" : "Điểm chưa hợp lệ",
          text: isFishingTable ? "Vốn phải lớn hơn 0." : "Điểm phải lớn hơn 0.",
          confirmButtonText: "Đã hiểu",
        });
        return;
      }
    }

    // Nếu tokenCost <= 0 thì bỏ qua trừ xu (dùng cho chế độ hack VIP).
    if (tokenCost > 0) {
      // Check if user has enough coins
      if (userCoins < tokenCost) {
        showInsufficientXuModal(tokenCost);
        return;
      }

      // Deduct tokenCost xu
      const ok = await deductCoins(tokenCost, "SPIN_START");
      if (!ok) {
        showInsufficientXuModal(tokenCost);
        return;
      }
    }

    setIsSpinning(true);

    if (useFishingCapital) {
      const cap = Math.floor(fishingCapitalVnd as number);
      const { small, auto, boss } = generateFishingTurnSplit(cap);
      setManualValues({ rounds: small, minBet: "1k" });
      setAutoValues({ rounds: auto, minBet: "" });
      setBossValues({ rounds: boss });
      setTimeSlotText("");
      setAnalysisLatencyMs(randomInt(10, 18));
      setFishingAnalysisComplete(true);
    } else {
      const inputRaw =
        targetPoints !== undefined ? String(targetPoints) : points.trim();
      const input = parseInt(inputRaw, 10);
      const suggestion = generateSpinSuggestions(input, userCoins, strategyHint);

      setManualValues({
        rounds: suggestion.manual.rounds,
        minBet: formatSlotBetLevel(suggestion.manual.min),
      });
      setAutoValues({
        rounds: suggestion.auto.rounds,
        minBet: formatSlotBetLevel(suggestion.auto.min),
      });
      setBossValues({ rounds: 0 });
      setTimeSlotText(buildTimeSlotText(suggestion.manual.rounds, suggestion.auto.rounds));
      setFishingAnalysisComplete(false);
    }
    setTimeout(() => setIsSpinning(false), 250);
  };

  const showInsufficientXuModal = (requiredAmount: number = 1) => {
    // Create modal element
    const modal = document.createElement('div');
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.9);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 10000;
      backdrop-filter: blur(8px);
      animation: modalFadeIn 0.3s ease-out;
    `;

    const modalContent = document.createElement('div');
    modalContent.style.cssText = `
      background-color: var(--x88-ui-bg);
      border: 2px solid var(--x88-ui-border);
      border-radius: 16px;
      padding: 35px;
      text-align: center;
      color: white;
      max-width: 420px;
      width: 90%;
      box-shadow: 0 16px 48px rgba(0, 0, 0, 0.5);
      position: relative;
      animation: modalSlideIn 0.4s ease-out;
      font-family: 'Courier New', monospace;
    `;

    modalContent.innerHTML = `
      <style>
        @keyframes modalFadeIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes modalSlideIn {
          0% { 
            opacity: 0;
            transform: translateY(-30px) scale(0.9);
            filter: blur(5px);
          }
          100% { 
            opacity: 1;
            transform: translateY(0) scale(1);
            filter: blur(0);
          }
        }
        @keyframes iconPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
        @keyframes textGlow {
          0%, 100% { text-shadow: 0 0 10px rgba(255, 107, 107, 0.5); }
          50% { text-shadow: 0 0 20px rgba(255, 107, 107, 0.8); }
        }
      </style>
      
      <div style="display: flex; align-items: center; margin-bottom: 25px; animation: textGlow 2s ease-in-out infinite;">
        <div style="
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, #ffeb3b, #ffc107);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 20px;
          font-size: 28px;
          color: #000;
          font-weight: bold;
          box-shadow: 
            0 0 20px rgba(255, 235, 59, 0.6),
            0 0 40px rgba(255, 193, 7, 0.3);
          animation: iconPulse 1.5s ease-in-out infinite;
          border: 2px solid rgba(255, 255, 255, 0.3);
        ">!</div>
        <h2 style="
          margin: 0; 
          color: #ff6b6b; 
          font-size: 26px; 
          font-weight: 900;
          text-shadow: 
            0 0 15px rgba(255, 107, 107, 0.8),
            0 0 25px rgba(255, 107, 107, 0.5);
          letter-spacing: 2px;
          text-transform: uppercase;
        ">KHÔNG ĐỦ XU!</h2>
      </div>
      
      <div style="margin-bottom: 20px; font-size: 16px;">
        <div style="
          margin-bottom: 12px; 
          padding: 8px 12px;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 8px;
          border: 1px solid rgba(255, 255, 255, 0.1);
        ">
          <span style="color: rgba(255, 255, 255, 0.9); font-weight: 500;">SỐ XU HIỆN TẠI: </span>
          <span style="
            color: #ffeb3b; 
            font-weight: bold; 
            font-size: 20px;
            text-shadow: 0 0 10px rgba(255, 235, 59, 0.6);
          ">${userCoins} XU</span>
        </div>
        <div style="
          margin-bottom: 12px; 
          padding: 8px 12px;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 8px;
          border: 1px solid rgba(255, 255, 255, 0.1);
        ">
          <span style="color: rgba(255, 255, 255, 0.9); font-weight: 500;">YÊU CẦU TỐI THIỂU: </span>
          <span style="
            color: #ffeb3b; 
            font-weight: bold; 
            font-size: 20px;
            text-shadow: 0 0 10px rgba(255, 235, 59, 0.6);
          ">${requiredAmount} XU</span>
        </div>
      </div>
      
      <div onclick="window.open('https://t.me/congnghemoi668', '_blank')" style="
        margin-bottom: 25px; 
        font-size: 14px; 
        color: #64b5f6;
        padding: 10px 15px;
        background: rgba(100, 181, 246, 0.1);
        border-radius: 4px;
        border: 1px solid rgba(100, 181, 246, 0.3);
        text-shadow: 0 0 10px rgba(100, 181, 246, 0.5);
        letter-spacing: 1px;
        cursor: pointer;
        transition: all 0.3s ease;
      " onmouseover="
        this.style.backgroundColor='rgba(100, 181, 246, 0.2)';
        this.style.borderColor='rgba(100, 181, 246, 0.5)';
        this.style.transform='scale(1.02)';
      " onmouseout="
        this.style.backgroundColor='rgba(100, 181, 246, 0.1)';
        this.style.borderColor='rgba(100, 181, 246, 0.3)';
        this.style.transform='scale(1)';
      ">
        » LIÊN HỆ ADMIN ĐỂ NẠP XU «
      </div>
      
      <button onclick="this.parentElement.parentElement.remove()" style="
        background: linear-gradient(45deg, #ff6b6b, #ee5a24);
        border: none;
        border-radius: 12px;
        padding: 14px 35px;
        color: white;
        font-size: 16px;
        font-weight: bold;
        cursor: pointer;
        transition: all 0.3s ease;
        box-shadow: 
          0 0 20px rgba(255, 107, 107, 0.4),
          0 4px 15px rgba(0, 0, 0, 0.3);
        text-transform: uppercase;
        letter-spacing: 1px;
        font-family: 'Courier New', monospace;
      " onmouseover="
        this.style.transform='translateY(-2px)';
        this.style.boxShadow='0 0 30px rgba(255, 107, 107, 0.6), 0 6px 20px rgba(0, 0, 0, 0.4)';
      " onmouseout="
        this.style.transform='translateY(0)';
        this.style.boxShadow='0 0 20px rgba(255, 107, 107, 0.4), 0 4px 15px rgba(0, 0, 0, 0.3)';
      ">
        ĐÓNG
      </button>
    `;

    modal.appendChild(modalContent);
    document.body.appendChild(modal);
  };

  const openHackPopup = () => {
    // Yêu cầu: bật popup lên thì ô input xóa trắng
    setHackBalance("");
    setHackPopupMode("input");
    setHackProgress(0);
    setHackCapitalLabel("");
    pendingFishingCapitalVndRef.current = null;
    if (isFishingTable) setFishingAnalysisComplete(false);
    setIsHackPopupOpen(true);
  };

  const closeHackPopup = () => {
    setIsHackPopupOpen(false);
    setHackPopupMode("input");
    setHackProgress(0);
    setHackCapitalLabel("");
    pendingFishingCapitalVndRef.current = null;
    hackRoundedValueRef.current = null;
    if (hackProgressTimerRef.current != null) {
      window.clearInterval(hackProgressTimerRef.current);
      hackProgressTimerRef.current = null;
    }
  };

  const openVipPopup = () => {
    const vipBetPool = SLOT_BET_LEVELS.filter((l) => l >= 8 && l <= 40);
    const betWeights = [28, 22, 18, 14, 10, 5, 3]; // ưu tiên 8→28
    const r = Math.random() * 100;
    let acc = 0;
    let pick = vipBetPool[0];
    for (let i = 0; i < vipBetPool.length; i++) {
      acc += betWeights[i] ?? 2;
      if (r <= acc) {
        pick = vipBetPool[i];
        break;
      }
    }
    setVipRecommendBet(formatSlotBetLevel(pick));

    // Theo yêu cầu:
    // - random 85 - 95%
    // - thắng lớn < thắng siêu lớn < thắng cực lớn (cực lớn là lớn nhất)
    // Để đảm bảo strict tăng trong khi giới hạn 95%, cho thắng lớn tối đa 93.
    const big = randomInt(85, 93);
    const superPct = randomInt(big + 1, 94);
    const ultraPct = randomInt(superPct + 1, 95);

    setVipPctBig(big);
    setVipPctSuper(superPct);
    setVipPctUltra(ultraPct);

    setIsVipPopupOpen(true);
  };

  const closeVipPopup = () => setIsVipPopupOpen(false);

  const handleVipFeatureClick = async () => {
    if (isVipHackPopupStarting) return;
    if (isVipFeatureCharging) return;

    if (userCoins < 20) {
      showInsufficientXuModal(20);
      return;
    }

    setIsVipFeatureCharging(true);
    const ok = await deductCoins(20, "VIP_FEATURE_OPEN");
    setIsVipFeatureCharging(false);
    if (!ok) {
      showInsufficientXuModal(20);
      return;
    }

    openVipPopup();
  };

  const formatMMSS = (totalSeconds: number) => {
    const s = Math.max(0, Math.floor(totalSeconds));
    const mm = String(Math.floor(s / 60)).padStart(2, "0");
    const ss = String(s % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  };

  const startVipHackNow = () => {
    if (vipHackActive || isVipHackPopupStarting) return;

    const vipMidPool = SLOT_BET_LEVELS.filter((l) => l >= 8 && l <= 40);
    const selectedVipBet =
      vipMidPool[randomInt(0, vipMidPool.length - 1)] ?? 8;
    setVipRecommendBet(formatSlotBetLevel(selectedVipBet));
    const vipCalcSeed = Math.max(20, Math.floor(selectedVipBet * randomInt(10, 22)));
    const vipSuggestion = generateSpinSuggestions(vipCalcSeed, userCoins, "auto");

    if (isFishingTable) {
      const cap = Math.max(
        Math.floor(selectedVipBet * 1000 * randomInt(5, 14)),
        15000,
        Math.min(500_000, Math.floor(userCoins * 50))
      );
      const { small, auto, boss } = generateFishingTurnSplit(cap);
      setManualValues({ rounds: small, minBet: "1k" });
      setAutoValues({ rounds: auto, minBet: "" });
      setBossValues({ rounds: boss });
      setTimeSlotText("");
      setAnalysisLatencyMs(randomInt(10, 18));
      setFishingAnalysisComplete(true);
    } else {
      setAnalysisLatencyMs(randomInt(10, 18));
      const { manual: manualBet, auto: autoBet } = pickSlotBetsForCapital(
        Math.max(vipCalcSeed, selectedVipBet * (vipSuggestion.manual.rounds + vipSuggestion.auto.rounds)),
        vipSuggestion.manual.rounds,
        vipSuggestion.auto.rounds
      );
      const manualLevel = Math.max(manualBet, selectedVipBet);
      const autoIdx = Math.min(
        indexOfSlotBet(manualLevel) + 1,
        SLOT_BET_LEVELS.length - 1
      );
      const autoLevel = Math.max(autoBet, SLOT_BET_LEVELS[autoIdx]);
      setManualValues({
        rounds: vipSuggestion.manual.rounds,
        minBet: formatSlotBetLevel(manualLevel),
      });
      setAutoValues({
        rounds: vipSuggestion.auto.rounds,
        minBet: formatSlotBetLevel(autoLevel),
      });
      setBossValues({ rounds: 0 });
      setTimeSlotText(
        buildTimeSlotText(vipSuggestion.manual.rounds, vipSuggestion.auto.rounds)
      );
    }

    // "2p trở xuống": random 60..120 giây
    const durationSec = Math.floor(60 + Math.random() * 61);

    setIsVipHackPopupStarting(true);
    setVipHackActive(true);
    setVipHackUiVisible(true);
    setVipHackRemainingSec(durationSec);
    setVipHackEndsAtMs(Date.now() + durationSec * 1000);

    // Đóng popup hack đang có để tránh che UI / xung đột
    setIsHackPopupOpen(false);
    setHackPopupMode("input");
    setHackProgress(0);
    setHackCapitalLabel("");
    hackRoundedValueRef.current = null;
    if (hackProgressTimerRef.current != null) {
      window.clearInterval(hackProgressTimerRef.current);
      hackProgressTimerRef.current = null;
    }

    // Bấm xong chạy ngay: đóng popup VIP liền, không dùng setTimeout 3s nữa
    setIsVipPopupOpen(false);
    setIsVipHackPopupStarting(false);
  };

  const stopVipHackNow = () => {
    if (!vipHackActive && !isVipHackPopupStarting) return;

    if (vipHackUiTimerRef.current != null) {
      window.clearInterval(vipHackUiTimerRef.current);
      vipHackUiTimerRef.current = null;
    }

    setVipHackActive(false);
    setVipHackUiVisible(false);
    setVipHackEndsAtMs(null);
    setVipHackRemainingSec(0);
    setIsVipHackPopupStarting(false);
  };

  useEffect(() => {
    if (!vipHackActive) return;
    if (vipHackEndsAtMs == null) return;

    if (vipHackUiTimerRef.current != null) {
      window.clearInterval(vipHackUiTimerRef.current);
      vipHackUiTimerRef.current = null;
    }

    vipHackUiTimerRef.current = window.setInterval(() => {
      const remaining = Math.max(
        0,
        Math.ceil((vipHackEndsAtMs - Date.now()) / 1000)
      );
      setVipHackRemainingSec(remaining);

      if (remaining <= 0) {
        if (vipHackUiTimerRef.current != null) {
          window.clearInterval(vipHackUiTimerRef.current);
          vipHackUiTimerRef.current = null;
        }
        setVipHackActive(false);
        setVipHackUiVisible(false);
        setVipHackEndsAtMs(null);
        setVipHackRemainingSec(0);
      }
    }, 200);

    return () => {
      if (vipHackUiTimerRef.current != null) {
        window.clearInterval(vipHackUiTimerRef.current);
        vipHackUiTimerRef.current = null;
      }
    };
  }, [vipHackActive, vipHackEndsAtMs]);

  const confirmHackPopup = async () => {
    if (isSpinning) return;

    const raw = hackBalance.trim();
    const value = parseInt(raw, 10);
    // Bỏ 3 số sau (vd 123000 -> 123, 124345 -> 124)
    const roundedValue = Number.isFinite(value) ? Math.floor(value / 1000) : NaN;

    if (!raw || Number.isNaN(roundedValue) || roundedValue <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Dữ liệu chưa hợp lệ",
        text: "Giá trị phải lớn hơn hoặc bằng 1000.",
        confirmButtonText: "Đã hiểu",
      });
      return;
    }

    const displayCapital = Math.floor(value / 1000) * 1000;
    pendingFishingCapitalVndRef.current = isFishingTable ? displayCapital : null;

    setHackPopupMode("loading");
    setHackCapitalLabel(
      Number.isFinite(displayCapital) ? displayCapital.toLocaleString("vi-VN") : ""
    );
    setHackProgress(0);
    hackRoundedValueRef.current = roundedValue;

    if (hackProgressTimerRef.current != null) {
      window.clearInterval(hackProgressTimerRef.current);
      hackProgressTimerRef.current = null;
    }

    // Chạy thanh tiến trình UI (mô phỏng "đang phân tích" như ảnh)
    hackProgressTimerRef.current = window.setInterval(() => {
      setHackProgress((p) => {
        const jump = 2 + Math.random() * 6;
        const next = Math.min(100, Math.floor(p + jump));
        return next;
      });
    }, 120);

    // Chạy logic trừ token + tính toán song song
    // Để đúng yêu cầu: phải chạy xong màn "đang phân tích" mới bắt đầu cập nhật kết quả.

    // Đóng popup ngoài cùng trong giai đoạn loading (để không bị "thừa popup")
    setIsHackPopupOpen(false);
  };

  useEffect(() => {
    if (hackPopupMode !== "loading") return;
    if (hackProgress < 100) return;

    if (hackProgressTimerRef.current != null) {
      window.clearInterval(hackProgressTimerRef.current);
      hackProgressTimerRef.current = null;
    }

    const rounded = hackRoundedValueRef.current;
    if (rounded == null) return;

    // Chờ handleStart cập nhật xong kết quả (quay mồi/quay auto / bắn cá) thì mới đóng popup
    void (async () => {
      const cap = pendingFishingCapitalVndRef.current;
      if (isFishingTable && cap != null && cap >= 1000) {
        await handleStart(undefined, 10, undefined, cap);
        pendingFishingCapitalVndRef.current = null;
      } else {
        await handleStart(rounded, 10);
      }
      closeHackPopup();
    })();
  }, [hackPopupMode, hackProgress]);

  const showAnalysisPanel =
    vipHackActive ||
    vipHackUiVisible ||
    (isFishingTable && fishingAnalysisComplete);
  const fishingUiTheme =
    isFishingTable &&
    (fishingAnalysisComplete || vipHackActive || vipHackUiVisible);
  const analysisValueColor = fishingUiTheme ? "#F7FF00" : "#00FF6F";
  const analysisBtnBorder = "1px solid var(--x88-ui-border)";
  const analysisBtnBg = "var(--x88-ui-bg)";
  const analysisBtnColor = fishingUiTheme ? "#ffffff" : "#00FF6F";

  return (
    <div className="nh-hub">
      <Header setIsShowLogout={() => setIsShowLogout(true)} />
      <div
        className="container-fluid lobby-bg position-relative mx-auto mb-5 max-w-screen-xl"
        style={{
          position: "relative",
          minHeight: "100vh",
          paddingLeft: "max(0px, env(safe-area-inset-left, 0px))",
          paddingRight: "max(0px, env(safe-area-inset-right, 0px))",
        }}
      >
      {/* Khung chính — nền giống /NH, /NH/slot (body + lobby-bg, không video) */}
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          paddingBottom: "max(16px, env(safe-area-inset-bottom, 0px))",
          /* Chừa khoảng dưới app header — nút Quay lại có top âm so với card */
          marginTop: isMobileUi ? 48 : 56,
          paddingLeft: "max(8px, env(safe-area-inset-left, 0px))",
          paddingRight: "max(8px, env(safe-area-inset-right, 0px))",
        }}
      >
        <div
          className={`nh-result-panel${isNarrow ? " nh-result-panel--narrow" : ""}`}
        >
          <button
            type="button"
            className={`nh-result-back${isMobileUi ? " nh-result-back--mobile" : ""}`}
            style={isMobileUi ? undefined : { left: -98 }}
            onClick={() =>
              window.history.length > 1
                ? navigate(-1)
                : navigate(isFishingTable ? "/fishing" : "/NH")
            }
            aria-label="Quay lại"
          >
            <img src="/assets/arrow-icon.png" alt="" />
            <span>Quay lại</span>
          </button>

          {isFishingTable ? (
            <p className="nh-result-winrate">
              <span>{"> "}</span>
              <span>Tỷ lệ: </span>
              <span style={{ color: "#F7FF00" }}>{winPercent}%</span>
              <span>{" <"}</span>
            </p>
          ) : null}

          <div className="nh-result-top">
            <img className="nh-result-robot" src="/assets/robot.gif" alt="Robot" />
            <div className="nh-result-progress">
              <img src="/assets/loading.gif" alt="" />
              <span className="nh-result-progress__pct">{winPercent}%</span>
            </div>
          </div>

          <div className="nh-result-game-block">
            <div className="nh-result-game-row">
              <div className="nh-result-game-thumb">
                <img
                  src={gameImg}
                  alt={gameTitle}
                  onError={(e) => ((e.currentTarget as HTMLImageElement).src = "/assets/nohu.gif")}
                />
              </div>
              <div className="nh-result-game-side">
                <h2 className="nh-result-reward-title" title={gameTitle}>
                  {gameTitle}
                </h2>
                <div className="nh-result-scan-wrap">
                {showAnalysisPanel ? (
                  <div
                    className="nh-result-analysis"
                    style={{
                      position: "absolute",
                      boxShadow: fishingUiTheme
                        ? "0 0 18px rgba(0,255,225,0.18)"
                        : "0 0 18px rgba(0,255,225,0.10)",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 900,
                        color: "#fff",
                        fontSize: isNarrow ? 11 : 16,
                        letterSpacing: 0.2,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <span>RNG:</span>
                      <span style={{ color: analysisValueColor }}>BẺ KHÓA</span>
                    </div>
                    <div
                      style={{
                        fontWeight: 900,
                        color: "#fff",
                        fontSize: isNarrow ? 11 : 16,
                        letterSpacing: 0.2,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <span>LATENCY:</span>
                      <span style={{ color: analysisValueColor }}>{`${analysisLatencyMs}ms`}</span>
                    </div>
                    <div
                      style={{
                        fontWeight: 900,
                        color: "#fff",
                        fontSize: isNarrow ? 11 : 16,
                        letterSpacing: 0.2,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <span>TỶ LỆ:</span>
                      <span style={{ color: "#FF1500" }}>BIẾN ĐỘNG CAO</span>
                    </div>
                    <button
                      type="button"
                      disabled
                      style={{
                        marginTop: 0,
                        border: analysisBtnBorder,
                        background: analysisBtnBg,
                        color: analysisBtnColor,
                        fontWeight: 900,
                        fontSize: isNarrow ? 10 : 15,
                        padding: isNarrow ? "3px 10px" : "5px 12px",
                        borderRadius: 10,
                        boxShadow: fishingUiTheme
                          ? "0 0 14px rgba(13,110,30,0.35)"
                          : "0 0 18px rgba(0,255,225,0.12)",
                        cursor: "not-allowed",
                        width: "100%",
                      }}
                    >
                      PHÂN TÍCH HOÀN TẤT
                    </button>
                  </div>
                ) : (
                  <div className="nh-result-scan" aria-label="Đang scanning">
                    <div className="nh-result-scan__brackets" aria-hidden="true">
                      <span className="nh-result-scan__corner nh-result-scan__corner--tl" />
                      <span className="nh-result-scan__corner nh-result-scan__corner--tr" />
                      <span className="nh-result-scan__corner nh-result-scan__corner--bl" />
                      <span className="nh-result-scan__corner nh-result-scan__corner--br" />
                      <span className="nh-result-scan__line" />
                    </div>
                    <span className="nh-result-scan__label">Scanning...</span>
                  </div>
                )}
                </div>
              </div>
            </div>
          </div>

          <div className="nh-result-grid">
            {isFishingTable ? (
              <>
                <div className="nh-result-stat-box">
                  <div className="nh-result-stat-box__title">Bắn mồi cá nhỏ</div>
                  <div className="nh-result-stat-box__sub">
                    {manualValues.rounds} lượt (Đạn 1k)
                  </div>
                </div>
                <div className="nh-result-stat-box">
                  <div className="nh-result-stat-box__title">Bắn auto cá lớn</div>
                  <div className="nh-result-stat-box__sub">{autoValues.rounds} lượt</div>
                </div>
                <div className="nh-result-stat-box">
                  <div className="nh-result-stat-box__title">Bắn boss đạn lớn</div>
                  <div className="nh-result-stat-box__sub">
                    {bossValues.rounds} lượt (Đạn 2K)
                  </div>
                </div>
                <div
                  className="nh-result-stat-box nh-result-stat-box--vip"
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    void handleVipFeatureClick();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      void handleVipFeatureClick();
                    }
                  }}
                >
                  <img className="nh-result-vip-icon" src="/assets/vip-icon.png" alt="" />
                  <div className="nh-result-stat-box__title">VIP Scanner</div>
                  <button
                    type="button"
                    className="nh-result-vip-btn"
                    disabled={isVipHackPopupStarting || isVipFeatureCharging}
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleVipFeatureClick();
                    }}
                  >
                    {"\u003E"}{" "}
                    {vipHackActive ? "KÍCH HOẠT LẠI" : "PHÂN TÍCH DIỆT BOSS"}{" "}
                    {"\u003C"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="nh-result-stat-box">
                  <div className="nh-result-stat-box__title">Quay mồi</div>
                  <div className="nh-result-stat-box__sub">
                    {manualValues.rounds} vòng - Mức cược {manualValues.minBet}
                  </div>
                </div>

                <div className="nh-result-stat-box">
                  <div className="nh-result-stat-box__title">Quay Auto</div>
                  <div className="nh-result-stat-box__sub">
                    {autoValues.rounds} vòng - Mức cược {autoValues.minBet}
                  </div>
                </div>

                <div className="nh-result-stat-box">
                  <div className="nh-result-stat-box__title">Khung giờ</div>
                  <div className="nh-result-stat-box__sub">{timeSlotText}</div>
                </div>

                <div
                  className="nh-result-stat-box nh-result-stat-box--vip"
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    void handleVipFeatureClick();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      void handleVipFeatureClick();
                    }
                  }}
                >
                  <img className="nh-result-vip-icon" src="/assets/vip-icon.png" alt="" />
                  <div className="nh-result-stat-box__title">VIP FEATURE</div>
                  <button
                    type="button"
                    className="nh-result-vip-btn"
                    disabled={isVipHackPopupStarting || isVipFeatureCharging}
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleVipFeatureClick();
                    }}
                  >
                    {"\u003E"} {vipHackActive ? "KÍCH HOẠT LẠI" : "KÍCH HOẠT NGAY"} {"\u003C"}
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Div 6: HACK / Status */}
          {vipHackUiVisible ? (
            <div
              style={{
                marginTop: 12,
                width: "100%",
                borderRadius: 14,
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                padding: "0 14px",
                gap: 8,
                minHeight: isNarrow ? 112 : 150,
              }}
            >
              <div
                style={{
                  height: 44,
                  width: "100%",
                  borderRadius: 12,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "var(--x88-ui-bg)",
                  border: "2px solid var(--x88-ui-border)",
                  color: "#ffffff",
                  fontWeight: 900,
                  fontSize: isNarrow ? 18 : 20,
                  boxShadow: "0 0 20px rgba(0, 255, 225, 0.35)",
                  letterSpacing: 0.5,
                }}
              >
                {formatMMSS(vipHackRemainingSec)}
              </div>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                }}
              >

                <button
                  type="button"
                  disabled={isSpinning || vipHackRemainingSec <= 0}
                  onClick={stopVipHackNow}
                  style={{
                    width: "100%",
                    height: 42,
                    border: "2px solid var(--x88-ui-border)",
                    borderRadius: 14,
                    background: "var(--x88-ui-bg)",
                    color: "#ffffff",
                    fontWeight: 900,
                    fontSize: 14,
                    cursor: "pointer",
                    boxShadow: "0 0 18px rgba(0, 255, 225, 0.35)",
                  }}
                >
                  DỪNG HACK NGAY
                </button>
              </div>
            </div>
          ) : isVipHackPopupStarting || vipHackActive ? (
            // Khi VIP hack vừa bấm (trong giai đoạn chờ 3s), ẩn hẳn nút HACK (10TOKEN)
            <div style={{ marginTop: 12, width: "100%", height: 110 }} />
          ) : hackPopupMode === "loading" ? (
            <div className="nh-result-loading-bar">
              <div className="nh-result-loading-bar__text">
                {isFishingTable ? (
                  <>
                    Đang phân tích dữ liệu với mức vốn{" "}
                    <span style={{ color: "#F7FF00" }}>{hackCapitalLabel}đ</span>...
                  </>
                ) : (
                  <>Đang phân tích dữ liệu với mức vốn {hackCapitalLabel}...</>
                )}
              </div>
              <div className="nh-result-loading-bar__track">
                <div
                  className="nh-result-loading-bar__fill"
                  style={{ width: `${hackProgress}%` }}
                />
              </div>
            </div>
          ) : !isHackPopupOpen ? (
            <button
              type="button"
              className="nh-result-hack-btn"
              disabled={isSpinning}
              onClick={openHackPopup}
            >
              HACK (10TOKEN)
            </button>
          ) : (
            <div className="nh-result-footer-spacer" aria-hidden="true" />
          )}
        </div>
      </div>

      {isHackPopupOpen && hackPopupMode === "input" ? (
        <div
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && hackPopupMode === "input") closeHackPopup();
          }}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 10001,
            background: "rgba(0,0,0,0.65)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backdropFilter: "blur(6px)",
            padding: 14,
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            style={{
              width: "min(430px, calc(100vw - 28px))",
              minHeight: isNarrow ? 150 : 265,
              height: isNarrow ? "auto" : 265,
              backgroundImage: "url('/assets/frame-result.png')",
              backgroundSize: "100% 100%",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              borderRadius: 16,
              padding: isNarrow ? 14 : 16,
              boxShadow: "0 0 35px rgba(0, 255, 225, 0.18)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: 12,
            }}
          >
            {hackPopupMode === "input" ? (
              <>
                {/* Item 1: Title */}
                <div
                  style={{
                    textAlign: "center",
                    fontSize: isNarrow ? 18 : 20,
                    fontWeight: 900,
                    color: "#F7FF00",
                    textShadow: "0 0 10px rgba(247,255,0,0.35)",
                    letterSpacing: 0.5,
                    marginBottom: -2,
                  }}
                >
                  {isFishingTable ? "NHẬP SỐ VỐN" : "NHẬP SỐ DƯ HIỆN TẠI"}
                </div>

                {/* Item 2: Input */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    height: 44,
                    borderRadius: 10,
                    border: "1px solid var(--x88-ui-border)",
                    background: "var(--x88-ui-bg)",
                    padding: "0 10px",
                    gap: 10,
                  }}
                >
                  <style>{`
                    /* Hide number spinners for the hack popup input only */
                    .hack-popup-number-input::-webkit-outer-spin-button,
                    .hack-popup-number-input::-webkit-inner-spin-button {
                      -webkit-appearance: none;
                      margin: 0;
                    }
                    .hack-popup-number-input {
                      -moz-appearance: textfield;
                      appearance: textfield;
                    }
                  `}</style>
                  <input
                    aria-label={isFishingTable ? "Nhập số vốn" : "Nhập số dư hiện tại"}
                    inputMode="numeric"
                    type="text"
                    pattern="[0-9]*"
                    value={hackBalance}
                    onChange={(e) => {
                      const next = e.target.value.replace(/\D/g, "");
                      setHackBalance(next);
                    }}
                    onKeyDown={(e) => {
                      if (e.ctrlKey || e.metaKey || e.altKey) return;
                      const allowed = [
                        "Backspace",
                        "Delete",
                        "Tab",
                        "ArrowLeft",
                        "ArrowRight",
                        "Home",
                        "End",
                      ];
                      if (allowed.includes(e.key)) return;
                      if (!/^\d$/.test(e.key)) e.preventDefault();
                    }}
                    className="hack-popup-number-input"
                    style={{
                      flex: 1,
                      border: "none",
                      outline: "none",
                      background: "transparent",
                      textAlign: "center",
                      fontSize: isNarrow ? 22 : 24,
                      fontWeight: 900,
                      color: "#F7FF00",
                    }}
                  />
                  <div
                    style={{
                      padding: "6px 14px",
                      borderRadius: 8,
                      color: "green",
                      fontWeight: 800,
                      fontSize: 14,
                    }}
                  >
                    vnd
                  </div>
                </div>

                {/* Item 3: Confirm button */}
                <button
                  type="button"
                  disabled={isSpinning}
                  onClick={confirmHackPopup}
                  style={{
                    width: "100%",
                    height: isNarrow ? 46 : 48,
                    border: "2px solid var(--x88-ui-border)",
                    borderRadius: 10,
                    background: "var(--x88-ui-bg)",
                    color: "#fff",
                    fontSize: isNarrow ? 16 : 18,
                    fontWeight: 900,
                    letterSpacing: 0.5,
                    cursor: isSpinning ? "not-allowed" : "pointer",
                    boxShadow: "0 0 25px rgba(0, 255, 225, 0.35)",
                    opacity: isSpinning ? 0.75 : 1,
                  }}
                >
                  BẮT ĐẦU QUÉT (10 TOKEN)
                </button>
              </>
            ) : (
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  padding: "12px 14px 14px",
                  borderRadius: 12,
                  boxSizing: "border-box",
                  backgroundImage: "url('/assets/frame-result.png')",
                  backgroundSize: "100% 100%",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center",
                }}
              >
                <div
                  style={{
                    color: isFishingTable ? "#ffffff" : "var(--x88-ui-border)",
                    fontWeight: 900,
                    fontSize: isNarrow ? 13 : 14,
                    textShadow: isFishingTable
                      ? "0 0 8px rgba(0,0,0,0.45)"
                      : "0 0 10px rgba(0,255,225,0.25)",
                    padding: "0 4px",
                    whiteSpace: isFishingTable ? "normal" : "nowrap",
                    overflow: isFishingTable ? "visible" : "hidden",
                    textOverflow: isFishingTable ? "clip" : "ellipsis",
                  }}
                >
                  {isFishingTable ? (
                    <>
                      Đang phân tích dữ liệu với mức vốn{" "}
                      <span style={{ color: "#F7FF00" }}>{hackCapitalLabel}đ</span>...
                    </>
                  ) : (
                    <>Đang phân tích dữ liệu với mức vốn {hackCapitalLabel}...</>
                  )}
                </div>

                <div
                  style={{
                    width: "100%",
                    height: 18,
                    borderRadius: 999,
                    background: "var(--x88-ui-bg)",
                    border: "1px solid var(--x88-ui-border)",
                    position: "relative",
                    overflow: "hidden",
                    boxShadow: isFishingTable
                      ? "0 0 18px rgba(255,149,0,0.15)"
                      : "0 0 22px rgba(0,255,225,0.12)",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: -2,
                      left: 0,
                      height: 22,
                      width: `${hackProgress}%`,
                      borderRadius: 999,
                      background: "linear-gradient(90deg, var(--x88-ui-border) 0%, #00ff9d 55%, #F7FF00 100%)",
                      boxShadow: isFishingTable
                        ? "0 0 18px rgba(255,149,0,0.35)"
                        : "0 0 22px rgba(0,255,225,0.25)",
                      transition: "width 0.12s linear",
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {isVipPopupOpen ? (
        <div
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeVipPopup();
          }}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 10002,
            background: "rgba(0,0,0,0.65)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backdropFilter: "blur(6px)",
            padding: 14,
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            style={{
              width: "min(360px, calc(100vw - 32px))",
              maxWidth: "100%",
              boxSizing: "border-box",
              background: "var(--x88-ui-bg)",
              border: "1px solid var(--x88-ui-border)",
              borderRadius: 14,
              boxShadow:
                "0 0 0 1px rgba(0, 255, 225, 0.35), 0 0 28px rgba(0, 255, 225, 0.45)",
              position: "relative",
              color: "#fff",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                minHeight: 34,
                padding: "6px 10px",
                borderBottom: "1px solid var(--x88-ui-border)",
                boxSizing: "border-box",
              }}
            >
              <button
                type="button"
                onClick={closeVipPopup}
                aria-label="Đóng"
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  border: "1px solid var(--x88-ui-border)",
                  background: "var(--x88-ui-bg)",
                  color: "#ff6666",
                  fontSize: 14,
                  lineHeight: 1,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 0,
                }}
              >
                ×
              </button>
            </div>

            <div
              style={{
                padding: "14px 18px 16px",
                display: "flex",
                flexDirection: "column",
                gap: 0,
              }}
            >
              <div
                style={{
                  textAlign: "center",
                  fontWeight: 900,
                  letterSpacing: 0.4,
                  color: "#ffff00",
                  fontSize: isNarrow ? 15 : 17,
                  textShadow: "0 0 12px rgba(255,255,0,0.35)",
                  textTransform: "uppercase",
                }}
              >
                TỶ LỆ THẮNG LỚN
              </div>

              <div
                style={{
                  marginTop: 12,
                  border: "1px solid var(--x88-ui-border)",
                  borderRadius: 10,
                  padding: "8px 10px",
                  textAlign: "center",
                  fontWeight: 800,
                  fontSize: isNarrow ? 12 : 13,
                  background: "var(--x88-ui-bg)",
                }}
              >
                <span style={{ color: "#ffffff" }}>MỨC CƯỢC ĐỀ NGHỊ: </span>
                <span style={{ color: "#ffff00" }}>{vipRecommendBet}</span>
              </div>

              <div
                style={{
                  marginTop: 12,
                  display: "grid",
                  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                  gap: 8,
                }}
              >
                <div
                  style={{
                    borderRadius: 10,
                    border: "1px solid var(--x88-ui-border)",
                    backgroundColor: "var(--x88-ui-bg)",
                    padding: "8px 4px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 800,
                      fontSize: 9,
                      lineHeight: 1.15,
                      color: "#ffffff",
                      textTransform: "uppercase",
                      letterSpacing: 0.02,
                    }}
                  >
                    THẮNG LỚN
                  </div>
                  <div
                    style={{
                      color: "#39ff14",
                      fontWeight: 900,
                      fontSize: isNarrow ? 18 : 20,
                      marginTop: 4,
                      textShadow: "0 0 10px rgba(57,255,20,0.45)",
                    }}
                  >
                    {vipPctBig}%
                  </div>
                </div>

                <div
                  style={{
                    borderRadius: 10,
                    border: "1px solid var(--x88-ui-border)",
                    backgroundColor: "var(--x88-ui-bg)",
                    padding: "8px 4px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 800,
                      fontSize: 9,
                      lineHeight: 1.15,
                      color: "#ffffff",
                      textTransform: "uppercase",
                    }}
                  >
                    THẮNG SIÊU LỚN
                  </div>
                  <div
                    style={{
                      color: "#ffff00",
                      fontWeight: 900,
                      fontSize: isNarrow ? 18 : 20,
                      marginTop: 4,
                      textShadow: "0 0 8px rgba(255,255,0,0.35)",
                    }}
                  >
                    {vipPctSuper}%
                  </div>
                </div>

                <div
                  style={{
                    borderRadius: 10,
                    border: "1px solid var(--x88-ui-border)",
                    backgroundColor: "var(--x88-ui-bg)",
                    padding: "8px 4px",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 800,
                      fontSize: 9,
                      lineHeight: 1.15,
                      color: "#ffffff",
                      textTransform: "uppercase",
                    }}
                  >
                    THẮNG CỰC LỚN
                  </div>
                  <div
                    style={{
                      color: "#ff4da6",
                      fontWeight: 900,
                      fontSize: isNarrow ? 18 : 20,
                      marginTop: 4,
                      textShadow: "0 0 10px rgba(255,77,166,0.45)",
                    }}
                  >
                    {vipPctUltra}%
                  </div>
                </div>
              </div>

              <button
                type="button"
                disabled={isVipHackPopupStarting || vipHackActive}
                onClick={startVipHackNow}
                style={{
                  marginTop: 14,
                  width: "100%",
                  height: 42,
                  border: "1px solid var(--x88-ui-border)",
                  borderRadius: 12,
                  background: "var(--x88-ui-bg)",
                  color: "#ffffff",
                  fontWeight: 900,
                  fontSize: isNarrow ? 13 : 14,
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                  boxShadow: "0 0 18px rgba(0, 255, 225, 0.35)",
                  cursor: isVipHackPopupStarting || vipHackActive ? "not-allowed" : "pointer",
                  opacity: isVipHackPopupStarting || vipHackActive ? 0.8 : 1,
                }}
              >
                DÙNG HACK NGAY
              </button>
            </div>
          </div>
        </div>
      ) : null}

      </div>
      <ModalConfirmLogout
        isShowLogout={isShowLogout}
        setIsShowLogout={() => {
          setIsShowLogout(false);
        }}
      />
    </div>
  );
};

export default TableGameNew;
